/**
 * Blocking Period Conflicts Modal
 * Shown when saving blocking periods on an inventory item that conflict
 * with existing bookings using that item. Lets the user reassign affected
 * bookings to alternative inventory items, or proceed with a warning.
 */

import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, Loader2, AlertTriangle, Check, Package, Search, ShieldAlert } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useDateFormatter } from '../hooks/useDateFormatter';
import { useAuth } from '../contexts/AuthContext';
import { insertAuditLogBatch } from '../services/bookingAuditLog.service';
import type { InventoryBlockingTime } from '../services/inventoryBlockingTimes.service';
import { formatAllocationFailure, type AllocationRpcResult } from '../lib/allocationRpc';

interface AffectedBooking {
  bookingItemId: string;
  bookingId: string;
  orderId: string;
  customerName: string;
  startDate: string;
  endDate: string;
  productTypeId: string;
  productName: string;
  currentSize: string | null;
  currentItemNumber: string | null;
}

interface AlternativeItem {
  id: string;
  nr: number | null;
  item_number: string | null;
  combination_code: string | null;
  size: string | null;
  active: boolean;
  isIdentical: boolean;
}

interface BlockingPeriodConflictsModalProps {
  isOpen: boolean;
  onClose: () => void;
  inventoryItemId: string;
  productTypeId: string;
  blockingTimes: InventoryBlockingTime[];
  onResolved: (resolutions: Array<{ bookingItemId: string; newInventoryItemId: string }>) => void;
  /** Called when user proceeds despite unresolved conflicts */
  onProceedWithConflicts: () => void;
}

export default function BlockingPeriodConflictsModal({
  isOpen,
  onClose,
  inventoryItemId,
  productTypeId,
  blockingTimes,
  onResolved,
  onProceedWithConflicts
}: BlockingPeriodConflictsModalProps) {
  const { t } = useTranslation();
  const { user, profile, merchantId: authMerchantId } = useAuth();
  const { formatDateShort } = useDateFormatter();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [affectedBookings, setAffectedBookings] = useState<AffectedBooking[]>([]);
  // Map from bookingItemId → list of alternatives
  const [alternativesMap, setAlternativesMap] = useState<Map<string, AlternativeItem[]>>(new Map());
  // Map from bookingItemId → selected alternative inventory item id
  const [selections, setSelections] = useState<Map<string, string>>(new Map());
  // Track which bookings user chose to skip (proceed without resolving)
  const [skipped, setSkipped] = useState<Set<string>>(new Set());
  // Search queries per booking
  const [searchQueries, setSearchQueries] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    if (isOpen && blockingTimes.length > 0) {
      loadConflicts();
      setSelections(new Map());
      setSkipped(new Set());
      setSearchQueries(new Map());
      setError(null);
    }
  }, [isOpen]);

  const loadConflicts = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Find all open/active bookings that use this inventory item
      // and overlap with any of the blocking periods
      const { data: bookingItems, error: biError } = await supabase
        .from('booking_items')
        .select(`
          id,
          inventory_item_id,
          product_type_id,
          bookings!inner(
            id,
            order_id,
            customer_firstname,
            customer_lastname,
            start_date,
            end_date,
            booking_state
          ),
          product_types!inner(
            product_name
          )
        `)
        .eq('inventory_item_id', inventoryItemId)
        .in('bookings.booking_state', ['open', 'active']);

      if (biError) throw biError;

      // Filter to only those that actually overlap with a blocking period
      const affected: AffectedBooking[] = [];

      for (const bi of (bookingItems || []) as any[]) {
        const booking = bi.bookings;
        if (!booking) continue;

        const bookingStart = new Date(booking.start_date);
        const bookingEnd = new Date(booking.end_date);

        // Check if this booking overlaps with ANY blocking period
        const overlaps = blockingTimes.some(bt => {
          const blockStart = new Date(bt.blocked_from);
          const blockEnd = new Date(bt.blocked_until);
          return blockStart < bookingEnd && blockEnd > bookingStart;
        });

        if (overlaps) {
          affected.push({
            bookingItemId: bi.id,
            bookingId: booking.id,
            orderId: booking.order_id,
            customerName: `${booking.customer_firstname || ''} ${booking.customer_lastname || ''}`.trim() || 'Unknown',
            startDate: booking.start_date,
            endDate: booking.end_date,
            productTypeId: bi.product_type_id,
            productName: (bi.product_types as any)?.product_name || 'Unknown Product',
            currentSize: null, // Will be populated below
            currentItemNumber: null,
          });
        }
      }

      if (affected.length === 0) {
        // No conflicts — caller should just proceed with save
        setAffectedBookings([]);
        setAlternativesMap(new Map());
        setLoading(false);
        return;
      }

      // Get current inventory item info for size/item_number display
      const { data: currentItem } = await supabase
        .from('inventory_items')
        .select('item_number, size')
        .eq('id', inventoryItemId)
        .single() as unknown as { data: { item_number: string | null; size: string | null } | null; error: any };

      if (currentItem) {
        for (const a of affected) {
          a.currentSize = currentItem.size;
          a.currentItemNumber = currentItem.item_number;
        }
      }

      // Find alternative inventory items of the same product type
      const { data: sameTypeItems, error: itemsError } = await supabase
        .from('inventory_items')
        .select('id, nr, item_number, combination_code, size, active')
        .eq('product_type_id', productTypeId)
        .neq('id', inventoryItemId)
        .eq('active', true)
        .order('nr', { ascending: true }) as unknown as {
          data: Array<{ id: string; nr: number | null; item_number: string | null; combination_code: string | null; size: string | null; active: boolean }> | null;
          error: any;
        };

      if (itemsError) throw itemsError;

      const candidateItems = sameTypeItems || [];

      // For each candidate, check:
      // 1. No booking overlap with any of the affected booking date ranges
      // 2. No blocking period overlap with any of the affected booking date ranges
      // We need to check per-booking, since each booking has different dates.

      // Get all booking conflicts for candidate items
      const candidateIds = candidateItems.map(i => i.id);

      let bookingConflicts: any[] = [];
      if (candidateIds.length > 0) {
        // Find all open/active bookings for candidate items
        const { data: conflicts, error: conflictsError } = await supabase
          .from('booking_items')
          .select(`
            id,
            inventory_item_id,
            bookings!inner(
              id,
              start_date,
              end_date,
              booking_state
            )
          `)
          .in('inventory_item_id', candidateIds)
          .in('bookings.booking_state', ['open', 'active']);

        if (conflictsError) throw conflictsError;
        bookingConflicts = conflicts || [];
      }

      // Get all blocking periods for candidate items
      let candidateBlockingTimes: any[] = [];
      if (candidateIds.length > 0) {
        const { data: blockings, error: blockingsError } = await supabase
          .from('inventory_blocking_times')
          .select('*')
          .in('inventory_item_id', candidateIds);

        if (blockingsError) throw blockingsError;
        candidateBlockingTimes = blockings || [];
      }

      // Build alternatives per affected booking
      const newAlternativesMap = new Map<string, AlternativeItem[]>();

      for (const booking of affected) {
        const bStart = new Date(booking.startDate);
        const bEnd = new Date(booking.endDate);

        const alternatives: AlternativeItem[] = [];

        for (const candidate of candidateItems) {
          // Check if this candidate has a booking conflict for this booking's dates
          const hasBookingConflict = bookingConflicts.some((c: any) => {
            if (c.inventory_item_id !== candidate.id) return false;
            const cb = c.bookings;
            if (!cb) return false;
            const cStart = new Date(cb.start_date);
            const cEnd = new Date(cb.end_date);
            return cStart < bEnd && cEnd > bStart;
          });

          if (hasBookingConflict) continue;

          // Check if this candidate has a blocking period conflict for this booking's dates
          const hasBlockingConflict = candidateBlockingTimes.some((bt: any) => {
            if (bt.inventory_item_id !== candidate.id) return false;
            const btStart = new Date(bt.blocked_from);
            const btEnd = new Date(bt.blocked_until);
            return btStart < bEnd && btEnd > bStart;
          });

          if (hasBlockingConflict) continue;

          const isIdentical = candidate.size === booking.currentSize;

          alternatives.push({
            ...candidate,
            isIdentical,
          });
        }

        // Sort: identical size first, then by nr
        alternatives.sort((a, b) => {
          if (a.isIdentical && !b.isIdentical) return -1;
          if (!a.isIdentical && b.isIdentical) return 1;
          return (a.nr || 0) - (b.nr || 0);
        });

        newAlternativesMap.set(booking.bookingItemId, alternatives);
      }

      setAffectedBookings(affected);
      setAlternativesMap(newAlternativesMap);
    } catch (err) {
      console.error('Error loading blocking period conflicts:', err);
      setError(t('blockingConflicts.failedToCheck'));
    } finally {
      setLoading(false);
    }
  }, [inventoryItemId, productTypeId, blockingTimes]);

  function handleSelect(bookingItemId: string, inventoryItemId: string) {
    setSelections(prev => {
      const next = new Map(prev);
      next.set(bookingItemId, inventoryItemId);
      return next;
    });
    // Un-skip if user selects an alternative
    setSkipped(prev => {
      const next = new Set(prev);
      next.delete(bookingItemId);
      return next;
    });
  }

  function handleSkipToggle(bookingItemId: string) {
    setSkipped(prev => {
      const next = new Set(prev);
      if (next.has(bookingItemId)) {
        next.delete(bookingItemId);
      } else {
        next.add(bookingItemId);
        // Clear selection when skipping
        setSelections(p => {
          const n = new Map(p);
          n.delete(bookingItemId);
          return n;
        });
      }
      return next;
    });
  }

  // Compute which alternatives are already "taken" by another booking's selection
  // to handle cascading: if item X is selected for booking A, it shouldn't be available for booking B
  function getAvailableAlternatives(bookingItemId: string): AlternativeItem[] {
    const allAlts = alternativesMap.get(bookingItemId) || [];

    // Collect all selected inventory item IDs from OTHER bookings
    const takenIds = new Set<string>();
    for (const [biId, selectedItemId] of selections) {
      if (biId !== bookingItemId) {
        takenIds.add(selectedItemId);
      }
    }

    // Mark taken items but still show them (as unavailable)
    return allAlts.filter(alt => !takenIds.has(alt.id));
  }

  // Check if all bookings are either resolved (have selection) or skipped
  const allHandled = affectedBookings.every(b => {
    return selections.has(b.bookingItemId) || skipped.has(b.bookingItemId);
  });

  const hasAnySkipped = skipped.size > 0;
  const hasAnyResolved = selections.size > 0;

  async function handleConfirm() {
    setSaving(true);
    setError(null);

    try {
      const resolutions: Array<{ bookingItemId: string; newInventoryItemId: string }> = [];

      for (const booking of affectedBookings) {
        const selectedId = selections.get(booking.bookingItemId);
        if (!selectedId) continue; // Skipped

        // Use the change_booking_allocation RPC to reassign
        const { data: rpcData, error: rpcError } = await (supabase.rpc as any)('change_booking_allocation', {
          p_booking_item_id: booking.bookingItemId,
          p_new_inventory_item_id: selectedId,
          p_swap_with_booking_item_id: null,
          p_current_inventory_item_id: inventoryItemId,
          p_skip_buffer_check: true, // We're doing maintenance blocking, buffer doesn't apply
        });

        if (rpcError) throw rpcError;
        const result = rpcData as AllocationRpcResult | null;
        if (!result?.ok) {
          throw new Error(result ? formatAllocationFailure(result, t) : t('changeAllocation.failedToChange'));
        }

        resolutions.push({
          bookingItemId: booking.bookingItemId,
          newInventoryItemId: selectedId,
        });
      }

      // Log each reallocation to the affected booking's audit log (fire-and-forget)
      if (resolutions.length > 0 && authMerchantId) {
        const auditEntries = resolutions.map(r => {
          const booking = affectedBookings.find(b => b.bookingItemId === r.bookingItemId);
          const selectedAlt = alternativesMap.get(r.bookingItemId)?.find(a => a.id === r.newInventoryItemId);
          return {
            bookingId: booking?.bookingId || '',
            merchantId: authMerchantId,
            changeType: 'inventory_reallocated',
            changeDetails: {
              product_name: booking?.productName || 'Unknown',
              from_item: booking?.currentItemNumber || null,
              to_item: selectedAlt?.item_number || (selectedAlt?.nr ? `#${selectedAlt.nr}` : null),
              reason: 'blocking_period',
            },
            userId: user?.id,
            userEmail: profile?.full_name || user?.email || null,
            triggeredBy: 'admin_panel',
          };
        });
        insertAuditLogBatch(auditEntries);
      }

      if (hasAnySkipped && !hasAnyResolved) {
        // All skipped, proceed with conflicts
        onProceedWithConflicts();
      } else if (hasAnySkipped) {
        // Some resolved, some skipped — resolve what we can, then proceed
        onResolved(resolutions);
      } else {
        // All resolved
        onResolved(resolutions);
      }
      onClose();
    } catch (err) {
      console.error('Error resolving blocking period conflicts:', err);
      const pgErr = err as any;
      if (pgErr?.code === '23P01') {
        setError(pgErr.message || t('blockingConflicts.itemNotAvailable'));
      } else {
        setError(t('blockingConflicts.failedToReassign'));
      }
    } finally {
      setSaving(false);
    }
  }

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 isolate">
      <div
        className="rounded-lg shadow-2xl w-full overflow-hidden flex flex-col mx-auto relative z-50 bg-primary"
        style={{ maxHeight: '90vh', maxWidth: '560px' }}
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-md sm:p-lg border-b border-primary bg-primary">
          <div className="flex items-center gap-md">
            <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-warning flex-shrink-0" />
            <div className="min-w-0">
              <h2 className="text-lg sm:text-xl font-bold text-primary">{t('blockingConflicts.title')}</h2>
              <p className="text-sm text-secondary">
                {loading
                  ? t('blockingConflicts.checking')
                  : t('blockingConflicts.bookingsAffected', { count: affectedBookings.length })
                }
              </p>
            </div>
          </div>
          <button onClick={onClose} className="btn-icon flex-shrink-0" title={t('blockingConflicts.close')}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-md sm:p-lg">
          {error && (
            <div className="p-md bg-danger/10 border-l-4 border-danger rounded-lg mb-lg">
              <p className="text-sm text-danger">{error}</p>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-2xl">
              <Loader2 className="w-6 h-6 text-blue animate-spin" />
              <span className="ml-md text-secondary">{t('blockingConflicts.loadingAffected')}</span>
            </div>
          ) : affectedBookings.length === 0 ? (
            <div className="text-center py-2xl">
              <Check className="w-12 h-12 text-success mx-auto mb-md" />
              <p className="text-primary font-medium">{t('blockingConflicts.noConflicts')}</p>
              <p className="text-sm text-secondary mt-xs">
                {t('blockingConflicts.noConflictsDescription')}
              </p>
            </div>
          ) : (
            <div className="space-y-xl">
              {affectedBookings.map(booking => {
                const availableAlts = getAvailableAlternatives(booking.bookingItemId);
                const selectedId = selections.get(booking.bookingItemId);
                const isSkipped = skipped.has(booking.bookingItemId);
                const searchQuery = searchQueries.get(booking.bookingItemId) || '';
                const filtered = searchQuery.trim()
                  ? availableAlts.filter(item =>
                      item.item_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      item.combination_code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      item.nr?.toString().includes(searchQuery) ||
                      item.size?.toLowerCase().includes(searchQuery.toLowerCase())
                    )
                  : availableAlts;

                return (
                  <div key={booking.bookingItemId} className="border border-primary rounded-lg overflow-hidden">
                    {/* Booking header */}
                    <div className="p-md bg-warning/5 border-b border-primary">
                      <div className="flex items-center gap-sm">
                        <Package className="w-4 h-4 text-warning flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <span className="font-medium text-primary">
                            {booking.productName}
                            {booking.currentSize ? ` (${booking.currentSize})` : ''}
                          </span>
                          <span className="text-sm text-secondary ml-sm">
                            — {booking.currentItemNumber || 'unassigned'}
                          </span>
                        </div>
                        {selectedId && (
                          <Check className="w-4 h-4 text-success flex-shrink-0" />
                        )}
                        {isSkipped && (
                          <ShieldAlert className="w-4 h-4 text-warning flex-shrink-0" />
                        )}
                      </div>
                      <div className="mt-xs text-xs text-secondary">
                        {t('blockingConflicts.booking')} {booking.orderId} — {booking.customerName}
                        <br />
                        {formatDateShort(booking.startDate)} — {formatDateShort(booking.endDate)}
                      </div>
                    </div>

                    {/* Alternatives */}
                    <div className="p-md">
                      {isSkipped ? (
                        <div className="p-sm bg-warning/5 border border-warning/30 rounded-lg">
                          <p className="text-sm text-warning">
                            {t('blockingConflicts.willRemainOnItem')}
                          </p>
                          <button
                            type="button"
                            onClick={() => handleSkipToggle(booking.bookingItemId)}
                            className="text-xs text-blue underline mt-xs"
                          >
                            {t('blockingConflicts.chooseAlternative')}
                          </button>
                        </div>
                      ) : availableAlts.length === 0 ? (
                        <div>
                          <p className="text-sm text-secondary py-sm">
                            {t('blockingConflicts.noAlternativesForDates')}
                          </p>
                          <label className="flex items-start gap-sm cursor-pointer mt-sm p-sm bg-warning/5 border border-warning/30 rounded-lg">
                            <input
                              type="checkbox"
                              checked={isSkipped}
                              onChange={() => handleSkipToggle(booking.bookingItemId)}
                              className="mt-0.5"
                            />
                            <span className="text-sm text-secondary">
                              {t('blockingConflicts.proceedAnyway')}
                            </span>
                          </label>
                        </div>
                      ) : (
                        <>
                          {/* Search (only show if many items) */}
                          {availableAlts.length > 5 && (
                            <div className="relative mb-md">
                              <Search className="absolute left-sm top-1/2 -translate-y-1/2 w-4 h-4 text-tertiary" />
                              <input
                                type="text"
                                value={searchQuery}
                                onChange={e => setSearchQueries(prev => new Map(prev).set(booking.bookingItemId, e.target.value))}
                                placeholder={t('resolveConflicts.searchItems')}
                                className="input input-sm pl-xl w-full"
                              />
                            </div>
                          )}

                          <div className="space-y-xs" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                            {filtered.map(item => {
                              const isSelected = selectedId === item.id;

                              return (
                                <button
                                  key={item.id}
                                  type="button"
                                  onClick={() => handleSelect(booking.bookingItemId, item.id)}
                                  className={`w-full flex items-center gap-md p-sm rounded-lg border text-left transition-colors ${
                                    isSelected
                                      ? 'border-blue bg-blue/10'
                                      : 'border-primary hover:border-blue hover:bg-blue/5'
                                  }`}
                                >
                                  {/* Selection indicator */}
                                  <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                                    isSelected ? 'border-blue bg-blue' : 'border-tertiary'
                                  }`}>
                                    {isSelected && <Check className="w-3 h-3 text-white" />}
                                  </div>

                                  {/* Item info */}
                                  <div className="flex-1 min-w-0 flex items-center gap-md flex-wrap">
                                    <span className="font-medium text-primary text-sm">
                                      {item.item_number || `#${item.nr}`}
                                    </span>
                                    {item.combination_code && (
                                      <span className="text-xs text-secondary">
                                        {item.combination_code}
                                      </span>
                                    )}
                                    {item.size && (
                                      <span className="text-xs text-secondary">
                                        {t('changeAllocation.sizeLabel')}: {item.size}
                                      </span>
                                    )}
                                  </div>

                                  {/* Badges */}
                                  <div className="flex items-center gap-xs flex-shrink-0">
                                    {item.isIdentical && (
                                      <span className="badge badge-success text-xs">{t('changeAllocation.sameSize')}</span>
                                    )}
                                    <span className="badge badge-success text-xs">{t('changeAllocation.available')}</span>
                                  </div>
                                </button>
                              );
                            })}
                          </div>

                          {/* Option to skip */}
                          <div className="mt-md">
                            <label className="flex items-start gap-sm cursor-pointer p-sm bg-warning/5 border border-warning/30 rounded-lg">
                              <input
                                type="checkbox"
                                checked={isSkipped}
                                onChange={() => handleSkipToggle(booking.bookingItemId)}
                                className="mt-0.5"
                              />
                              <span className="text-sm text-secondary">
                                {t('blockingConflicts.skipProceed')}
                              </span>
                            </label>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-md sm:p-lg border-t border-primary bg-primary">
          <div className="flex-1 min-w-0">
            {hasAnySkipped && (
              <div className="flex items-center gap-sm text-sm text-warning">
                <ShieldAlert className="w-4 h-4 flex-shrink-0" />
                <span>
                  {t('blockingConflicts.willRemainCount', { count: skipped.size })}
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-sm flex-shrink-0">
            <button onClick={onClose} className="btn btn-secondary" disabled={saving}>
              {t('common.cancel')}
            </button>
            {affectedBookings.length === 0 ? (
              <button
                onClick={() => { onProceedWithConflicts(); onClose(); }}
                className="btn btn-primary"
                disabled={loading}
              >
                <Check className="w-4 h-4" />
                {t('blockingConflicts.continue')}
              </button>
            ) : (
              <button
                onClick={handleConfirm}
                disabled={!allHandled || saving || loading}
                className={hasAnySkipped && !hasAnyResolved ? 'btn btn-warning' : 'btn btn-primary'}
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('resolveConflicts.applying')}
                  </>
                ) : hasAnySkipped && !hasAnyResolved ? (
                  <>
                    <ShieldAlert className="w-4 h-4" />
                    {t('blockingConflicts.proceedAnywayButton')}
                  </>
                ) : hasAnySkipped ? (
                  <>
                    <AlertTriangle className="w-4 h-4" />
                    {t('blockingConflicts.applyAndProceed')}
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    {t('blockingConflicts.reassignAndSave')}
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
